import {
  keepPreviousData,
  type QueryKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import type { SmsConversationDetail } from '../../types/messaging';
import type {
  PaginatedVoicemails,
  VoicemailDetail,
  VoicemailListItem,
} from '../../types/voicemail';
import { api, apiEndpoints } from '../../utils/apiService';
import type { Folder } from './voicemailModel';

export const LIST_PAGE_SIZE = 25;
export const POLL_MS = 30_000;
const API_MAX_PAGE_SIZE = 100;

async function readError(response: Response | null, fallback: string) {
  if (!response) return fallback;
  const body = (await response.json().catch(() => null)) as {
    detail?: string;
  } | null;
  return body?.detail || fallback;
}

async function getJson<T>(url: string, fallback: string): Promise<T> {
  const response = await api.get(url);
  if (!response?.ok) throw new Error(await readError(response, fallback));
  return response.json();
}

export interface ListParams {
  folder: Folder;
  unread: boolean;
  limit: number;
}

export interface ListResult {
  rows: VoicemailListItem[];
  total: number;
}

/** The newest `limit` voicemails in a folder, newest first. */
export async function fetchVoicemails({
  folder,
  unread,
  limit,
}: ListParams): Promise<ListResult> {
  const rows: VoicemailListItem[] = [];
  const pageSize = Math.min(limit, API_MAX_PAGE_SIZE);
  let total = 0;
  for (let page = 1; ; page++) {
    const data = await getJson<PaginatedVoicemails>(
      apiEndpoints.voicemails({
        folder,
        unread: folder === 'inbox' && unread ? true : undefined,
        page,
        page_size: pageSize,
      }),
      'Voicemails could not be loaded.',
    );
    rows.push(...(data.results ?? []));
    total = data.count ?? rows.length;
    if (!data.next || rows.length >= limit) break;
  }
  return { rows: rows.slice(0, limit), total };
}

function useVoicemailKeys() {
  const lists = useScopedQueryKey('voicemails');
  const unread = useScopedQueryKey('voicemail-unread-count');
  const detail = useScopedQueryKey('voicemail');
  return useMemo(
    () => ({
      lists: lists as QueryKey,
      unread: unread as QueryKey,
      detail: (id: number) => [...detail, id] as QueryKey,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(lists), JSON.stringify(unread), JSON.stringify(detail)],
  );
}

export function useVoicemailList(params: ListParams) {
  const keys = useVoicemailKeys();
  return useQuery({
    queryKey: [...keys.lists, params.folder, params.unread, params.limit],
    queryFn: () => fetchVoicemails(params),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });
}

export function useVoicemail(id: number | null) {
  const keys = useVoicemailKeys();
  return useQuery({
    queryKey: keys.detail(id ?? 0),
    queryFn: () =>
      getJson<VoicemailDetail>(
        apiEndpoints.voicemailDetail(id as number),
        'This voicemail could not be loaded.',
      ),
    enabled: id != null,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });
}

export function useVoicemailActions() {
  const queryClient = useQueryClient();
  const keys = useVoicemailKeys();

  /** PATCH /api/voicemails/{id}/update/ { read } | { archived } */
  const update = useCallback(
    async (id: number, payload: { read?: boolean; archived?: boolean }) => {
      const response = await api.patch(
        apiEndpoints.voicemailUpdate(id),
        payload,
      );
      if (!response?.ok) {
        throw new Error(
          await readError(response, 'The voicemail could not be updated.'),
        );
      }
      const updated = (await response.json()) as VoicemailDetail;
      // A detail fetch started before the write would land stale on top.
      await queryClient.cancelQueries({ queryKey: keys.detail(id) });
      queryClient.setQueryData(keys.detail(id), updated);
      queryClient.setQueriesData<ListResult>(
        { queryKey: keys.lists },
        (current) =>
          current && {
            ...current,
            rows: current.rows.map((r) =>
              r.id === id ? { ...r, ...updated } : r,
            ),
          },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.lists }),
        queryClient.invalidateQueries({ queryKey: keys.unread }),
      ]);
    },
    [queryClient, keys],
  );

  /**
   * POST /api/voicemails/{id}/reply/ { body } → the caller's SMS
   * conversation. The turn shows as Sending meanwhile; on failure it is
   * removed (the voicemail thread never shows failed sends) and the caller
   * keeps the draft.
   */
  const reply = useCallback(
    async (
      id: number,
      body: string,
      me: { id: number; email: string } | null,
    ) => {
      const key = keys.detail(id);
      const now = new Date().toISOString();
      const pendingId = -Date.now();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<VoicemailDetail>(key);
      if (previous) {
        const conversation =
          previous.sms_conversation ??
          ({ messages: [] } as unknown as SmsConversationDetail);
        queryClient.setQueryData<VoicemailDetail>(key, {
          ...previous,
          sms_conversation: {
            ...conversation,
            messages: [
              ...conversation.messages,
              {
                id: pendingId,
                direction: 'OUTBOUND',
                sender_phone_e164: '',
                recipient_phone_e164: previous.caller_phone,
                body,
                provider: '',
                provider_message_sid: '',
                provider_status: '',
                status: 'QUEUED',
                error_code: '',
                error_message: '',
                sent_by_user: me?.id ?? null,
                sent_by_user_email: me?.email ?? null,
                sent_by_agent: false,
                message_type: 'TEXT',
                num_segments: 1,
                price: null,
                created_at: now,
                updated_at: now,
                delivered_at: null,
                failed_at: null,
              },
            ],
          },
        });
      }
      const restore = () => {
        if (previous) queryClient.setQueryData(key, previous);
      };
      let response: Response | null;
      try {
        response = await api.post(apiEndpoints.voicemailReply(id), { body });
      } catch (e) {
        restore();
        throw e;
      }
      if (!response?.ok) {
        restore();
        throw new Error(
          await readError(response, 'The reply could not be sent.'),
        );
      }
      const conversation = (await response.json()) as SmsConversationDetail;
      queryClient.setQueryData<VoicemailDetail>(
        key,
        (current) =>
          current && {
            ...current,
            sms_conversation: {
              ...conversation,
              messages: conversation.messages.filter(
                (m) => m.status !== 'FAILED',
              ),
            },
          },
      );
      await queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, keys],
  );

  /** POST /api/voicemails/mark-all-read/ — unread inbox messages. */
  const markAllRead = useCallback(async () => {
    const response = await api.post(apiEndpoints.voicemailMarkAllRead());
    if (!response?.ok) {
      throw new Error(await readError(response, 'Could not mark all as read.'));
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.lists }),
      queryClient.invalidateQueries({ queryKey: keys.unread }),
      queryClient.invalidateQueries({
        queryKey: keys.detail(0).slice(0, -1),
      }),
    ]);
  }, [queryClient, keys]);

  return { update, reply, markAllRead };
}
